import React, { useState } from 'react';
import { LoadingAnimation, SimpleBlock, Text } from 'ui-builder';
import ServiceProvidersGroupSelector from './ServiceProvidersGroupSelector';
import Label from '../../shared/uibuilder/form/layout/Label';
import { InformationMessage } from 'features/nekst-widgets';

import styles from './ClientPortalConfigurationForm.module.scss';
import Row6x6x from '../../shared/web/uitheme/form/Row6x6x';
import {
  AbstractTask,
  useProjectTasksService,
  TASK_CREATED,
  TASK_UPDATED,
  TASK_DELETED,
} from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';
import { useOpenUpdateFormHelper } from '../view/tasks/CreateUpdate/OpenUpdateFormField';

interface RoadmapTask {
  taskId: number;
  description: string | null;
}

export function RoadmapTaskDisplayWidget(props: {
  task: AbstractTask,
  roadmapDescription: string | null;
}) {
  const prefix = `roadmap.task${props.task.id}`;
  const openUpdateHelper = useOpenUpdateFormHelper();
  const projectTaskService = useProjectTasksService();

  const [loading, setLoading] = useState(false);

  const openTask = async () => {
    if (!loading) {
      setLoading(true);
      try {
        const task = await projectTaskService.getTask(props.task.id);
        openUpdateHelper.openForm(task);
      } finally {
        setLoading(false);
      }
    }
  };

  const getDescriptionPreview = () => {
    let result = props.roadmapDescription || props.task.description;

    if (result) {
      // Parse HTML safely using DOMParser
      const parser = new DOMParser();
      const doc = parser.parseFromString(result, 'text/html');

      // Replace mention elements with their text representation
      const mentionElements = doc.querySelectorAll('span.mention');
      mentionElements.forEach((element) => {
        const textNode = doc.createTextNode(`#${element.getAttribute('data-value') || ''}`);
        element.replaceWith(textNode);
      });

      // Extract plain text content
      result = doc.body.textContent || '';
    }

    return result;
  };

  return (
    <Row6x6x className={styles.taskRow}>
      <div>
        <div
          onClick={openTask}
          className={`${styles.roadmapTaskName} ${loading ? styles.loading : ''}`}
        >
          {props.task._renderedName}
        </div>
        {getDescriptionPreview() && (
          <div className={styles.roadmapTaskDescription}>
            {getDescriptionPreview()}
          </div>
        )}
      </div>
      <ServiceProvidersGroupSelector
        source={`${prefix}.serviceProviders`}
        label="Include Service Providers"
      />
    </Row6x6x>
  );
}

export default function RoadmapWidget(
  props: {
    projectId: number,
    roadmapTasks: RoadmapTask[],
    label: string,
  },
) {
  const projectTaskService = useProjectTasksService();

  const loadTasks = async () => {
    const tasks = await projectTaskService.getAllTasks(
      props.projectId,
      {},
      {
        limit: 1000,
        offset: 0,
      },
    );

    return tasks.data;
  };

  const { data: allTasks, loading } = useDataLoaderNew(
    loadTasks,
    {
      dependencies: [props.projectId],
      events: [TASK_CREATED, TASK_UPDATED, TASK_DELETED],
    },
  );

  if (!loading && allTasks) {
    // Filter tasks that match roadmap task IDs
    const roadmapTaskIds = props.roadmapTasks.map((rt) => rt.taskId);
    const filteredTasks = allTasks.filter((task) => roadmapTaskIds.includes(task.id));

    // Create a map for quick description lookup
    const descriptionMap = new Map(
      props.roadmapTasks.map((rt) => [rt.taskId, rt.description])
    );

    return (
      <SimpleBlock className={styles.keyDateWidget}>
        <Label label={props.label} className={styles.label} />
        <InformationMessage>
          These tasks will appear in the client portal roadmap. Click a task name to edit its roadmap settings.
        </InformationMessage>
        {filteredTasks.length === 0 ? (
          <Text>
            No tasks are included in the roadmap. Check &quot;Portal Roadmap&quot; in task forms to add tasks.
          </Text>
        ) : (
          <SimpleBlock className={styles.tasks}>
            {filteredTasks.map((task) => (
              <RoadmapTaskDisplayWidget
                task={task}
                roadmapDescription={descriptionMap.get(task.id) || null}
                key={`roadmap-task-${task.id}`}
              />
            ))}
          </SimpleBlock>
        )}
      </SimpleBlock>
    );
  } else {
    return (<LoadingAnimation />);
  }
}
